import { FC } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartEvent,
  ActiveElement,
  ScriptableContext,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { KODAWARI } from '@doda/common/src/constants/seo/kodawari';
import {
  UPPER_AND_LOWER_HA,
  OP_VALUE_MAPPINGS,
  ANALYTICS_OP_NAME,
  ANALYTICS_HA_NAME,
  ANALYTICS_ES_NAME,
} from '@doda/common/src/constants/seo/jobTrendConstants';
import {
  LanguageSkillsRequestCode,
  PositionsRequestCode,
  RequirementsRequestCode,
  WorkplaceEnvironmentRequestCode,
  HolidayAndWayOfWorkingRequestCode,
  WelfareBenefitsRequestCode,
  EmployeeAverageAgeRequestCode,
  EmploymentStatusRequestCode,
  EstablishRequestCode,
  LowerLimitIncomeRequestCode,
} from '@doda/api-client/aspida/@types';
import { useAppDispatch } from '@doda/common/src/redux/hooks';
import {
  JobRecommendOffsetState,
  setAnnualIncome300to400,
  setAnnualIncome400to500,
  setAnnualIncome500to600,
  setAnnualIncome600to700,
  setAnnualIncome700to800,
  setAnnualIncome800to900,
  setAnnualIncome900to1000,
  setAnnualIncomeMoreThan1000,
  setChildcareAndChildcareSupportSystem,
  setChinese,
  setCommutingByCar,
  setCompanyHousingAndEentSubsidySystem,
  setConsecutiveVacations,
  setContractEmployee,
  setEmploysManyPeople,
  setEnglish,
  setEstablish10Year,
  setEstablish1Year,
  setEstablish50Year,
  setEstablishMoreThan50Year,
  setExpert,
  setFemaleThriving,
  setFewerRetirees,
  setFiveDayWorkweekSystem,
  setFlexibleWork,
  setForeignCompany,
  setForties,
  setFreeDressCode,
  setFullTrainingSystem,
  setHoliday,
  setIndustryInexperiencedWelcome,
  setInexperiencedOccupationWelcome,
  setListedCompany,
  setLittleOvertime,
  setManager,
  setMaternityAndChildcareLeave,
  setMoreThan120DaysOffAYear,
  setNoAcademicBackgroundRequired,
  setNoChangeInJobTitle,
  setNoOvertime,
  setNoTransfer,
  setOther,
  setOverFifties,
  setOverseasBusinessTrip,
  setQualificationAcquisitionSupportSystem,
  setRegularEmployee,
  setRetirementBenefitPlan,
  setSalaryOf250kYenMore,
  setSalaryOf350kYenMore,
  setSecondTimeGraduate,
  setThirties,
  setTwenties,
  setUIturnSupport,
  setUrgentRecruit,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendOffset';
import { ACCETABLE_LOWER_LIMIT_INCOME_VALUES } from '@doda/common/src/constants/accetableRequestValues';
import { toNewQueryValue } from '@doda/common/src/logics/createJobSearchRequestParams';
import {
  getJobListForTrendEmployeeAverageAge,
  getJobListForTrendEmploymentStatus,
  getJobListForTrendEstablish,
  getJobListForTrendHolidayAndWayOfWorking,
  getJobListForTrendIncome,
  getJobListForTrendLanguageAndPositions,
  getJobListForTrendRequirements,
  getJobListForTrendWelfareBenefits,
  getJobListForTrendWorkplaceEnvironment,
} from '@doda/common/src/services/getJobListForTrendAPI';
import { createNewRequestParamsForApi } from '@doda/common/src/logics/JobContent/JobTrend/conversionForApi';
import { setInitialDisplayFlagIncome } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendIncome';
import { setInitialDisplayFlagEstablish } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendEstablish';
import { setInitialDisplayFlagEmploymentStatus } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendEmploymentStatus';
import { setInitialDisplayFlagEmployeeAverageAge } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendEmployeeAverageAge';
import { setInitialDisplayFlagLanguageAndPositions } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendLanguageAndPositions';
import { setInitialDisplayFlagWelfareBenefits } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendWelfareBenefits';
import { setInitialDisplayFlagHolidayAndWayOfWorking } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendHolidayAndWayOfWorking';
import { setInitialDisplayFlagWorkplaceEnvironment } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendWorkplaceEnvironment1';
import { setInitialDisplayFlagRequirements } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendRequirements';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { measurejobtrendGraph } from '@doda/common/src/logics/JobContent/JobTrend/analytics';
import { MasterState } from '@doda/common/src/redux/features/master';
import { AppQueryParams } from '@doda/common/src/constants/queryParams';
import { createRandomOffset } from '@doda/common/src/logics/JobContent/JobTrend/createRandomOffset';
import { splitByLength } from '@doda/common/src/logics/JobContent/JobTrend/createArrayForBargraph';
import { useBargraphSelected } from '../../../../hooks/JobContent/JobTrend/useBargraphSelected';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

type SetSelected =
  | ReturnType<typeof useBargraphSelected>['setRequirementsSelected']
  | ReturnType<typeof useBargraphSelected>['setIncomeSelected']
  | ReturnType<typeof useBargraphSelected>['setEmployeeAverageAgeSelected']
  | ReturnType<typeof useBargraphSelected>['setPositionsSelected'];

type SetSubSelected =
  | ReturnType<typeof useBargraphSelected>['setPositionsSelected']
  | null;

/**
 * BargraphForJobTrendのProps
 * @param jobData ソートされたカテゴリの求人数配列
 * @param joblabel ソートされたカテゴリの項目名配列
 * @param setSelected クリックされた項目名と求人数をState管理するためのsetState
 * @param setSubSelected クリックされた項目名と求人数をState管理するためのsetState（仕事内容・語学用で仕事内容をセットする）
 * @param masterCache マスタキャッシュされた勤務地・職種・業種のマスタ
 * @param queryParamsForTrend クエリパラメータ(例：st: [a,s,d])
 * @param jobRecommendOffset useSelectorで取得したoffset一覧（５４個分）
 * @param bargraphSelectedName 初期表示されている項目名
 * @param userAgent PCデザインかSPデザインか
 * @param useV2JobSearchListApi V2求人一覧検索API利用可否
 */
interface Props {
  jobData: number[];
  joblabel: string[];
  setSelected: SetSelected;
  setSubSelected: SetSubSelected;
  masterCache: MasterState;
  queryParamsForTrend: AppQueryParams;
  jobRecommendOffset: JobRecommendOffsetState;
  bargraphSelectedName: string | undefined;
  userAgent: string;
  useV2JobSearchListApi: boolean;
}

export const BargraphForJobTrend: FC<Props> = ({
  joblabel,
  jobData,
  setSelected,
  setSubSelected,
  masterCache,
  queryParamsForTrend,
  jobRecommendOffset,
  bargraphSelectedName,
  userAgent,
  useV2JobSearchListApi,
}) => {
  const labels = joblabel.map((label) => String(label));
  // データラベル折り返し用処理
  let newLabels: string[][];
  if (userAgent === 'PC') {
    newLabels = labels.map((label) => {
      return label.length <= 6 || jobData.length <= 6
        ? [label]
        : splitByLength(label, 7);
    });
  } else {
    newLabels = labels.map((label) => {
      return label.length <= 5 ? [label] : splitByLength(label, 5);
    });
  }

  const newJobData = jobData.map((jobdata) => Number(jobdata));
  const dispatch = useAppDispatch();
  const handleClick = (e: ChartEvent, el: ActiveElement[], ins: ChartJS) => {
    const appRequestParams = createNewRequestParamsForApi({
      query: queryParamsForTrend,
      areas: masterCache.area,
      cities: masterCache.city,
      occupations: masterCache.occupation,
      industries: masterCache.industry,
    }).requestParams;

    // clickした棒グラフのインデックス（0からスタート）
    const clickedElementIndex = ins.scales.x.getValueForPixel(
      (e.native as MouseEvent).clientX - ins.canvas.getBoundingClientRect().left
    );
    // clickした棒グラフのラベル名
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: labels は string[][] なのに clickedElementIndex は number で違う型の値でインデクシングしているので、要修正
    const beforeClickedLabel = data.labels[clickedElementIndex];
    let clickedLabel = '';
    if (beforeClickedLabel) {
      clickedLabel = beforeClickedLabel.join('');
    }
    // clickした棒グラフのデータ
    const clickedValue = data.datasets[0].data[clickedElementIndex as number];
    let randomOffset: number;

    // グラフクリック時に計測発火
    let analyticsValue = '';
    const requirementsId =
      OP_VALUE_MAPPINGS.requirementsList[
        (KODAWARI.requirements.find((op) => op.text === clickedLabel)?.value ||
          '') as keyof typeof OP_VALUE_MAPPINGS.requirementsList
      ];
    const workplaceEnvironmentId =
      OP_VALUE_MAPPINGS.workplaceEnvironmentList[
        (KODAWARI.workplaceEnvironment.find((op) => op.text === clickedLabel)
          ?.value ||
          '') as keyof typeof OP_VALUE_MAPPINGS.workplaceEnvironmentList
      ];
    const holidayAndWayOfWorkingId =
      OP_VALUE_MAPPINGS.holidayAndWayOfWorkingList[
        (KODAWARI.holidayAndWayOfWorking.find((op) => op.text === clickedLabel)
          ?.value ||
          '') as keyof typeof OP_VALUE_MAPPINGS.holidayAndWayOfWorkingList
      ];
    const employeeAverageAgeId =
      OP_VALUE_MAPPINGS.employeeAverageAgeList[
        (KODAWARI.employeeAverageAge.find((op) => op.text === clickedLabel)
          ?.value ||
          '') as keyof typeof OP_VALUE_MAPPINGS.employeeAverageAgeList
      ];
    const employmentStatusId =
      clickedLabel ===
      KODAWARI.employmentStatus.find((op) => op.value === '3')?.text
        ? ['19']
        : OP_VALUE_MAPPINGS.employmentStatusList[
            (KODAWARI.employmentStatus.find((op) => op.text === clickedLabel)
              ?.value ||
              '') as keyof typeof OP_VALUE_MAPPINGS.employmentStatusList
          ];
    const welfareBenefitsId =
      OP_VALUE_MAPPINGS.welfareBenefitsList[
        (KODAWARI.welfareBenefits.find((op) => op.text === clickedLabel)
          ?.value || '') as keyof typeof OP_VALUE_MAPPINGS.welfareBenefitsList
      ];
    const positionsId =
      OP_VALUE_MAPPINGS.positionsList[
        (KODAWARI.positions.find((op) => op.text === clickedLabel)?.value ||
          '') as keyof typeof OP_VALUE_MAPPINGS.positionsList
      ];
    const languageSkillsId =
      OP_VALUE_MAPPINGS.languageSkillsList[
        (KODAWARI.languageSkills.find((op) => op.text === clickedLabel)
          ?.value || '') as keyof typeof OP_VALUE_MAPPINGS.languageSkillsList
      ];
    const haLowerId = UPPER_AND_LOWER_HA.find(
      (ha) => ha.name === clickedLabel
    )?.lowerId;
    const haUpperId = UPPER_AND_LOWER_HA.find(
      (ha) => ha.name === clickedLabel
    )?.upperId;
    const esId = KODAWARI.establish.find(
      (es) => es.text === clickedLabel
    )?.value;
    if (
      requirementsId ||
      workplaceEnvironmentId ||
      holidayAndWayOfWorkingId ||
      employeeAverageAgeId ||
      employmentStatusId ||
      welfareBenefitsId ||
      positionsId ||
      languageSkillsId
    ) {
      if (requirementsId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${requirementsId}`;
      }
      if (workplaceEnvironmentId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${workplaceEnvironmentId}`;
      }
      if (holidayAndWayOfWorkingId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${holidayAndWayOfWorkingId}`;
      }
      if (employeeAverageAgeId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${employeeAverageAgeId}`;
      }
      if (employmentStatusId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${employmentStatusId}`;
      }
      if (welfareBenefitsId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${welfareBenefitsId}`;
      }
      if (positionsId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${positionsId}`;
      }
      if (languageSkillsId) {
        analyticsValue = `${ANALYTICS_OP_NAME}${languageSkillsId}`;
      }
    }
    if (haLowerId && haUpperId) {
      analyticsValue = `${ANALYTICS_HA_NAME}${haLowerId}%2C${haUpperId}`;
    }
    if (esId) {
      analyticsValue = `${ANALYTICS_ES_NAME}${esId}`;
    }

    dispatch(
      measurejobtrendGraph({
        analyticsValue,
      })
    );
    // #region fields
    KODAWARI.requirements.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        if (appRequestParams.requirements) {
          appRequestParams.requirements = [
            String(appRequestParams.requirements),
            kodawari.value,
          ] as RequirementsRequestCode[];
        } else {
          appRequestParams.requirements = [
            kodawari.value as RequirementsRequestCode,
          ];
        }
        dispatch(setInitialDisplayFlagRequirements(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.inexperiencedOccupationWelcome) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setInexperiencedOccupationWelcome(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.inexperiencedOccupationWelcome;
            }
            break;
          case '2':
            if (!jobRecommendOffset.industryInexperiencedWelcome) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setIndustryInexperiencedWelcome(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.industryInexperiencedWelcome;
            }
            break;
          case '3':
            if (!jobRecommendOffset.noAcademicBackgroundRequired) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setNoAcademicBackgroundRequired(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.noAcademicBackgroundRequired;
            }
            break;
          case '4':
            if (!jobRecommendOffset.noTransfer) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setNoTransfer(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.noTransfer;
            }
            break;
          case '5':
            if (!jobRecommendOffset.secondTimeGraduate) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setSecondTimeGraduate(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.secondTimeGraduate;
            }
            break;
          case '6':
            if (!jobRecommendOffset.expert) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setExpert(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.expert;
            }
            break;
          case '7':
            if (!jobRecommendOffset.urgentRecruit) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setUrgentRecruit(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.urgentRecruit;
            }
            break;
          case '8':
            if (!jobRecommendOffset.employsManyPeople) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setEmploysManyPeople(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.employsManyPeople;
            }
            break;
          case '9':
            if (!jobRecommendOffset.noChangeInJobTitle) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setNoChangeInJobTitle(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.noChangeInJobTitle;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendRequirements({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.workplaceEnvironment.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        if (appRequestParams.workplaceEnvironment) {
          appRequestParams.workplaceEnvironment = [
            String(appRequestParams.workplaceEnvironment),
            kodawari.value,
          ] as WorkplaceEnvironmentRequestCode[];
        } else {
          appRequestParams.workplaceEnvironment = [
            kodawari.value as WorkplaceEnvironmentRequestCode,
          ];
        }
        dispatch(setInitialDisplayFlagWorkplaceEnvironment(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.listedCompany) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setListedCompany(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.listedCompany;
            }
            break;
          case '2':
            if (!jobRecommendOffset.foreignCompany) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setForeignCompany(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.foreignCompany;
            }
            break;
          case '3':
            if (!jobRecommendOffset.fewerRetirees) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setFewerRetirees(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.fewerRetirees;
            }
            break;
          case '4':
            if (!jobRecommendOffset.femaleThriving) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setFemaleThriving(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.femaleThriving;
            }
            break;
          case '5':
            if (!jobRecommendOffset.commutingByCar) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setCommutingByCar(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.commutingByCar;
            }
            break;
          case '6':
            if (!jobRecommendOffset.freeDressCode) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setFreeDressCode(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.freeDressCode;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendWorkplaceEnvironment({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.holidayAndWayOfWorking.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        if (appRequestParams.holidayAndWayOfWorking) {
          appRequestParams.holidayAndWayOfWorking = [
            String(appRequestParams.holidayAndWayOfWorking),
            kodawari.value,
          ] as HolidayAndWayOfWorkingRequestCode[];
        } else {
          appRequestParams.holidayAndWayOfWorking = [
            kodawari.value as HolidayAndWayOfWorkingRequestCode,
          ];
        }
        dispatch(setInitialDisplayFlagHolidayAndWayOfWorking(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.fiveDayWorkweekSystem) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setFiveDayWorkweekSystem(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.fiveDayWorkweekSystem;
            }
            break;
          case '2':
            if (!jobRecommendOffset.MoreThan120DaysOffAYear) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setMoreThan120DaysOffAYear(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.MoreThan120DaysOffAYear;
            }
            break;
          case '3':
            if (!jobRecommendOffset.holiday) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setHoliday(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.holiday;
            }
            break;
          case '4':
            if (!jobRecommendOffset.consecutiveVacations) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setConsecutiveVacations(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.consecutiveVacations;
            }
            break;
          case '5':
            if (!jobRecommendOffset.noOvertime) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setNoOvertime(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.noOvertime;
            }
            break;
          case '6':
            if (!jobRecommendOffset.littleOvertime) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setLittleOvertime(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.littleOvertime;
            }
            break;
          case '7':
            if (!jobRecommendOffset.flexibleWork) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setFlexibleWork(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.flexibleWork;
            }
            break;
          case '8':
            if (!jobRecommendOffset.maternityAndChildcareLeave) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setMaternityAndChildcareLeave(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.maternityAndChildcareLeave;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendHolidayAndWayOfWorking({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.welfareBenefits.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        if (appRequestParams.welfareBenefits) {
          appRequestParams.welfareBenefits = [
            String(appRequestParams.welfareBenefits),
            kodawari.value,
          ] as WelfareBenefitsRequestCode[];
        } else {
          appRequestParams.welfareBenefits = [
            kodawari.value as WelfareBenefitsRequestCode,
          ];
        }

        dispatch(setInitialDisplayFlagWelfareBenefits(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.retirementBenefitPlan) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setRetirementBenefitPlan(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.retirementBenefitPlan;
            }
            break;
          case '2':
            if (!jobRecommendOffset.companyHousingAndEentSubsidySystem) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setCompanyHousingAndEentSubsidySystem(randomOffset));
            } else {
              randomOffset =
                jobRecommendOffset.companyHousingAndEentSubsidySystem;
            }
            break;
          case '3':
            if (!jobRecommendOffset.childcareAndChildcareSupportSystem) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setChildcareAndChildcareSupportSystem(randomOffset));
            } else {
              randomOffset =
                jobRecommendOffset.childcareAndChildcareSupportSystem;
            }
            break;
          case '4':
            if (!jobRecommendOffset.qualificationAcquisitionSupportSystem) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setQualificationAcquisitionSupportSystem(randomOffset));
            } else {
              randomOffset =
                jobRecommendOffset.qualificationAcquisitionSupportSystem;
            }
            break;
          case '5':
            if (!jobRecommendOffset.fullTrainingSystem) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setFullTrainingSystem(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.fullTrainingSystem;
            }
            break;
          case '6':
            if (!jobRecommendOffset.salaryOf250kYenMore) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setSalaryOf250kYenMore(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.salaryOf250kYenMore;
            }
            break;
          case '7':
            if (!jobRecommendOffset.salaryOf350kYenMore) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setSalaryOf350kYenMore(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.salaryOf350kYenMore;
            }
            break;
          case '8':
            if (!jobRecommendOffset.UIturnSupport) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setUIturnSupport(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.UIturnSupport;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendWelfareBenefits({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.languageSkills.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: setSubSelected が null の可能性を考慮する
        setSubSelected(undefined);
        if (appRequestParams.languageSkills) {
          appRequestParams.languageSkills = [
            String(appRequestParams.languageSkills),
            kodawari.value,
          ] as LanguageSkillsRequestCode[];
        } else {
          appRequestParams.languageSkills = [
            kodawari.value,
          ] as LanguageSkillsRequestCode[];
        }

        dispatch(setInitialDisplayFlagLanguageAndPositions(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.english) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setEnglish(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.english;
            }
            break;
          case '2':
            if (!jobRecommendOffset.chinese) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setChinese(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.chinese;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendLanguageAndPositions({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.positions.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel && setSubSelected) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected(undefined);
        setSubSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        if (appRequestParams.positions) {
          appRequestParams.positions = [
            String(appRequestParams.positions),
            kodawari.value,
          ] as PositionsRequestCode[];
        } else {
          appRequestParams.positions = [
            kodawari.value,
          ] as PositionsRequestCode[];
        }

        dispatch(setInitialDisplayFlagLanguageAndPositions(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.overseasBusinessTrip) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setOverseasBusinessTrip(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.overseasBusinessTrip;
            }
            break;
          case '2':
            if (!jobRecommendOffset.manager) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setManager(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.manager;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendLanguageAndPositions({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.employeeAverageAge.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        appRequestParams.employeeAverageAge = [
          kodawari.value as EmployeeAverageAgeRequestCode,
        ];

        dispatch(setInitialDisplayFlagEmployeeAverageAge(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.twenties) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setTwenties(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.twenties;
            }
            break;
          case '2':
            if (!jobRecommendOffset.thirties) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setThirties(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.thirties;
            }
            break;
          case '3':
            if (!jobRecommendOffset.forties) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setForties(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.forties;
            }
            break;
          case '4':
            if (!jobRecommendOffset.overFifties) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setOverFifties(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.overFifties;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendEmployeeAverageAge({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.employmentStatus.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        appRequestParams.employmentStatus = [
          kodawari.value as EmploymentStatusRequestCode,
        ];

        dispatch(setInitialDisplayFlagEmploymentStatus(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.regularEmployee) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setRegularEmployee(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.regularEmployee;
            }
            break;
          case '2':
            if (!jobRecommendOffset.contractEmployee) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setContractEmployee(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.contractEmployee;
            }
            break;
          case '3':
            if (!jobRecommendOffset.other) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setOther(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.other;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendEmploymentStatus({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    KODAWARI.establish.forEach(async (kodawari) => {
      if (kodawari.text === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          value: kodawari.value,
          text: kodawari.text,
          count: clickedValue,
        });
        appRequestParams.establish = [kodawari.value as EstablishRequestCode];

        dispatch(setInitialDisplayFlagEstablish(1));

        switch (kodawari.value) {
          case '1':
            if (!jobRecommendOffset.establish1Year) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setEstablish1Year(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.establish1Year;
            }
            break;
          case '2':
            if (!jobRecommendOffset.establish10Year) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setEstablish10Year(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.establish10Year;
            }
            break;
          case '3':
            if (!jobRecommendOffset.establish50Year) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setEstablish50Year(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.establish50Year;
            }
            break;
          case '4':
            if (!jobRecommendOffset.establishMoreThan50Year) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setEstablishMoreThan50Year(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.establishMoreThan50Year;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendEstablish({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
    // #region fields
    UPPER_AND_LOWER_HA.forEach(async (ha) => {
      if (ha.name === clickedLabel) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO: 型を合わせる
        setSelected({
          id: ha.id,
          lowerId: ha?.lowerId,
          upperId: ha?.upperId,
          name: ha?.name,
          count: clickedValue,
        });

        appRequestParams.lowerLimitIncome = toNewQueryValue(
          ha.lowerId,
          ACCETABLE_LOWER_LIMIT_INCOME_VALUES
        ) as LowerLimitIncomeRequestCode;
        appRequestParams.upperLimitIncome = toNewQueryValue(
          ha.upperId,
          ACCETABLE_LOWER_LIMIT_INCOME_VALUES
        ) as LowerLimitIncomeRequestCode;

        dispatch(setInitialDisplayFlagIncome(1));

        switch (ha.id) {
          case '1':
            if (!jobRecommendOffset.annualIncome300to400) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome300to400(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome300to400;
            }
            break;
          case '2':
            if (!jobRecommendOffset.annualIncome400to500) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome400to500(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome400to500;
            }
            break;
          case '3':
            if (!jobRecommendOffset.annualIncome500to600) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome500to600(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome500to600;
            }
            break;
          case '4':
            if (!jobRecommendOffset.annualIncome600to700) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome600to700(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome600to700;
            }
            break;
          case '5':
            if (!jobRecommendOffset.annualIncome700to800) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome700to800(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome700to800;
            }
            break;
          case '6':
            if (!jobRecommendOffset.annualIncome800to900) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome800to900(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome800to900;
            }
            break;
          case '7':
            if (!jobRecommendOffset.annualIncome900to1000) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncome900to1000(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncome900to1000;
            }
            break;
          case '8':
            if (!jobRecommendOffset.annualIncomeMoreThan1000) {
              randomOffset = createRandomOffset(Number(clickedValue));
              dispatch(setAnnualIncomeMoreThan1000(randomOffset));
            } else {
              randomOffset = jobRecommendOffset.annualIncomeMoreThan1000;
            }
            break;
          default:
            break;
        }

        await dispatch(
          getJobListForTrendIncome({
            params: appRequestParams,
            offset: randomOffset,
            useV2JobSearchListApi,
          })
        );
      }
    });
    // #endregion
  };

  const selectOrHoverBackgroundColor = 'rgba(255, 241, 0, 0.25)';
  const defaultBackgroundColor = 'rgba(44, 171, 159, 0.25)';
  const borderColor = 'rgba(44, 171, 159, 1)';
  let barThicknessVar;
  let fontColor;
  let fontFamily;
  let data: {
    labels: string[][];
    datasets: {
      label: string;
      data: number[];
      backgroundColor(
        context: ScriptableContext<'bar'>
      ): 'rgba(255, 241, 0, 0.25)' | 'rgba(44, 171, 159, 0.25)';
      hoverBackgroundColor?: () => string;
      borderColor: string;
      borderWidth: number;
      barThickness: number;
    }[];
  };
  if (userAgent === 'PC') {
    barThicknessVar = 70;
    fontColor = '#222222';
    fontFamily =
      '游ゴシック体,YuGothic,游ゴシック Medium,Yu Gothic Medium,游ゴシック,Yu Gothic';
    data = {
      labels: newLabels,
      datasets: [
        {
          label: 'Dataset 1',
          data: newJobData,
          backgroundColor(context) {
            if (labels[context.dataIndex] === bargraphSelectedName) {
              return selectOrHoverBackgroundColor;
            }
            return defaultBackgroundColor;
          },
          hoverBackgroundColor() {
            return selectOrHoverBackgroundColor;
          },
          borderColor,
          borderWidth: 1,
          barThickness: barThicknessVar,
        },
      ],
    };
  } else {
    barThicknessVar = 50;
    fontColor = '#333333';
    fontFamily = 'Hiragino Kaku Gothic Pro';
    data = {
      labels: newLabels,
      datasets: [
        {
          label: 'Dataset 1',
          data: newJobData,
          backgroundColor(context: ScriptableContext<'bar'>) {
            if (labels[context.dataIndex] === bargraphSelectedName) {
              return selectOrHoverBackgroundColor;
            }
            return defaultBackgroundColor;
          },
          borderColor,
          borderWidth: 1,
          barThickness: barThicknessVar,
        },
      ],
    };
  }

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        formatter: (value: string) => {
          return value.toLocaleString();
        },
        display: true,
        anchor: 'end' as const,
        align: 'end' as const,
        font: {
          size: 10,
          family: fontFamily,
          color: fontColor,
        },
      },
      title: {
        display: true,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          min: 0.1,
          max: 0.8,
          color: fontColor,
          font: {
            size: 12,
            family: fontFamily,
            weight: 'bold',
          },
        },
        grid: {
          color: '#CCCCCC',
        },
      },
      y: {
        beginAtZero: true,
        min: 0.01,
        max: 1.2 * Math.max(...jobData),
        ticks: {
          display: false,
        },
        display: false,
      },
    },
    onClick: handleClick,
  };

  return <Bar data={data} options={options} />;
};
